import { useState, useEffect, useRef } from "react"
import { AlertTriangle, ClipboardCheck, CheckCircle2, Clock, Info, Loader2, Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "@/components/ui/dialog"

import { ClassroomPicker } from "../ClassRooms/ClassroomPicker"

import { environment } from "@/env/env.prod"
import { apiHeaders } from "@/Auth/api-headers."
import { SubjectCombobox } from "./SubjectCombobox"
import { lectureTimeError } from "./lectureTime"

// ─────────────────────────────────────────────────────────────────────────
// Embeddable "Take Attendance" action — a trigger button + the full
// classroom-pick → roster → tick/untick → submit dialog. No page wrapper
// of its own; the caller (AttendanceRegistry) supplies the surrounding page.
// ─────────────────────────────────────────────────────────────────────────

type ClassroomOption = { id: string; name: string; programme_id?: string | null }

// A register already in this classroom on the date (getClassroomDayRegisters).
// Someone else's comes back as taken only: no id, subject or names.
// The day's registers this faculty member has: their own and the ones shared with them.
type DayRegister = { mine: boolean; shared?: boolean; takenByName?: string | null; sessionId?: string; subjectCode?: string; subjectName?: string | null; period: string | null; startTime: string | null; endTime: string | null }

// The attendance policy in force for the classroom on the date (only what
// the form needs). No policy anywhere = the form as it always was.
type FormSlot = { name: string; start?: string; end?: string }
type FormPolicy = {
    subject: "select" | "none"
    slots: FormSlot[]
    duplicateWarning: boolean
    source: string | null   // the policy's name, for the note under the form
}
type RosterStudent = { uid: string; name?: string; eid?: string; joinedAt?: number | null }

// Mirrors AttendanceStatus.js on the backend. "present" and "absent" are the
// two the tick cycles between; the rest are picked deliberately. Anything not
// in this list can still be sent as free text — the server keeps the wording
// and treats it like an absence for the percentage.
const STATUS_OPTIONS = [
    { value: "absent", label: "Absent", counted: true },
    { value: "leave", label: "On leave", counted: false },
    { value: "excused", label: "Excused", counted: false },
]

function joinedLabel(ts?: number | null) {
    if (!ts) return null
    return new Date(ts).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
}

async function apiCall<T>(
    segment: "classrooms" | "attendance",
    path: string,
    opts: { method?: "GET" | "POST"; params?: Record<string, any>; body?: any } = {}
): Promise<T> {
    const { method = "GET", params, body } = opts
    let url = `${environment.BASE_URL}/${segment}/${path}`
    if (params) {
        const q = Object.entries(params)
            .filter(([ , v ]) => v !== undefined && v !== null && v !== "")
            .map(([ k, v ]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
            .join("&")
        if (q) url += `?${q}`
    }
    const res = await fetch(url, {
        method,
        headers: apiHeaders() as HeadersInit,
        body: body ? JSON.stringify(body) : undefined,
    })
    const json = await res.json()
    if (!json.success) throw new Error(json.message || "Request failed")
    return json
}

export default function TakeAttendanceAction({ onSubmitted, onOpenRegister }: { onSubmitted?: () => void; onOpenRegister?: (sessionId: string) => void } = {}) {
    const [ open, setOpen ] = useState(false)

    // classroom (ClassroomPicker: department → programme, or search)
    const [ selectedClassroom, setSelectedClassroom ] = useState<ClassroomOption | null>(null)

    // lecture details
    const [ lectureDate, setLectureDate ] = useState(() => new Date().toISOString().slice(0, 10))
    const [ subjectCode, setSubjectCode ] = useState("")
    const [ subjectName, setSubjectName ] = useState("")
    // optional: a lecture slot label and/or its time range ("HH:MM", both or neither)
    const [ period, setPeriod ] = useState("")
    const [ startTime, setStartTime ] = useState("")
    const [ endTime, setEndTime ] = useState("")
    const timeError = lectureTimeError(startTime, endTime)

    // the classroom's attendance policy: no subject -> no subject field;
    // fixed slots -> pick one instead of typing period / times
    const [ policy, setPolicy ] = useState<FormPolicy | null>(null)
    const [ loadingPolicy, setLoadingPolicy ] = useState(false)
    const policySeq = useRef(0)
    const noSubject = policy?.subject === "none"
    const slots = policy?.slots || []

    // roster + attendance state
    const [ roster, setRoster ] = useState<RosterStudent[]>([])
    const [ loadingRoster, setLoadingRoster ] = useState(false)
    const [ ticks, setTicks ] = useState<Record<string, boolean>>({})
    // Why a non-present student is not present. Only read when the tick is off;
    // defaults to "absent" so the common case needs no interaction.
    const [ statuses, setStatuses ] = useState<Record<string, string>>({})
    const [ rosterSearch, setRosterSearch ] = useState("")

    // client-side — the whole roster is already loaded (no backend
    // pagination), this just narrows what's rendered
    const filteredRoster = rosterSearch.trim()
        ? roster.filter(m => {
            const q = rosterSearch.trim().toLowerCase()
            return (m.name || "").toLowerCase().includes(q) || (m.eid || "").toLowerCase().includes(q)
        })
        : roster

    const [ submitting, setSubmitting ] = useState(false)
    const [ resultMsg, setResultMsg ] = useState<string | null>(null)
    const [ resultIsError, setResultIsError ] = useState(false)

    // Policy for the chosen classroom on the chosen date. A failure (server
    // without policies, network) leaves the form as it always was.
    useEffect(() => {
        if (!selectedClassroom) { setPolicy(null); return }
        const seq = ++policySeq.current
        setLoadingPolicy(true)
        apiCall<{ success: true; settings: { subject?: string; slots?: FormSlot[]; duplicateWarning?: boolean }; source: { level: string; name?: string } }>(
            "attendance", "getResolvedPolicy", { params: { classRoomId: selectedClassroom.id, date: lectureDate } }
        ).then(res => {
            if (seq !== policySeq.current) return
            const next: FormPolicy | null = res.source?.level === "default" ? null : {
                subject: res.settings?.subject === "none" ? "none" : "select",
                slots: Array.isArray(res.settings?.slots) ? res.settings.slots : [],
                duplicateWarning: res.settings?.duplicateWarning === true,
                source: res.source?.name || null,
            }
            setPolicy(next)
            // what the policy rules out is cleared, so it is never sent
            if (next?.subject === "none") {
                setSubjectCode(""); setSubjectName("")
                // whole-class attendance has no lecture details either
                if (!next.slots.length) { setPeriod(""); setStartTime(""); setEndTime("") }
            }
            if (next?.slots.length === 1) {
                // one slot: nothing to choose, it is that slot
                const only = next.slots[ 0 ]
                setPeriod(only.name); setStartTime(only.start || ""); setEndTime(only.end || "")
            } else if (next?.slots.length) {
                setPeriod(p => (next.slots.some(sl => sl.name === p) ? p : ""))
                setStartTime(""); setEndTime("")
                const kept = next.slots.find(sl => sl.name === period)
                if (kept) { setStartTime(kept.start || ""); setEndTime(kept.end || "") }
            }
        }).catch(() => {
            if (seq === policySeq.current) setPolicy(null)
        }).finally(() => {
            if (seq === policySeq.current) setLoadingPolicy(false)
        })
    }, [ selectedClassroom, lectureDate ])

    // The classroom's registers on the date: "already taken" / "not taken yet".
    const [ dayRegisters, setDayRegisters ] = useState<DayRegister[]>([])
    const daySeq = useRef(0)
    const loadDay = () => {
        if (!selectedClassroom || !lectureDate) { setDayRegisters([]); return }
        const seq = ++daySeq.current
        apiCall<{ success: true; registers: DayRegister[] }>("attendance", "getClassroomDayRegisters", { params: { classRoomId: selectedClassroom.id, date: lectureDate } })
            .then(res => { if (seq === daySeq.current) setDayRegisters(res.registers || []) })
            .catch(() => { if (seq === daySeq.current) setDayRegisters([]) })   // older server: no hints, form as before
    }
    useEffect(loadDay, [ selectedClassroom, lectureDate ])
    const takenSlot = (name: string) => dayRegisters.filter(r => (r.period || "").trim().toLowerCase() === name.trim().toLowerCase())
    // Same classroom + date (+ slot when there is one) as a register that exists.
    const duplicates = (() => {
        if (!policy?.duplicateWarning || dayRegisters.length === 0) return []
        const p = period.trim().toLowerCase()
        if (slots.length > 0) return p ? takenSlot(p) : []
        return dayRegisters.filter(r => (r.period || "").trim().toLowerCase() === p)
    })()

    const pickSlot = (slot: FormSlot) => {
        setPeriod(slot.name)
        setStartTime(slot.start || "")
        setEndTime(slot.end || "")
    }

    const selectClassroom = async (c: ClassroomOption) => {
        setSelectedClassroom(c)
        setLoadingRoster(true)
        setRoster([])
        setRosterSearch("")
        setResultMsg(null)
        try {
            const res = await apiCall<{ success: true; members: RosterStudent[] }>(
                "classrooms", "getClassRoomMembers", { params: { classRoomId: c.id } }
            )
            setRoster(res.members)
            const t: Record<string, boolean> = {}
            res.members.forEach(m => { t[ m.uid ] = true }) // default: everyone present
            setTicks(t)
        } catch (e: any) {
            setResultIsError(true)
            setResultMsg(e.message || "Failed to load roster")
        } finally {
            setLoadingRoster(false)
        }
    }

    const checkAllPresent = () => {
        const t: Record<string, boolean> = {}
        roster.forEach(m => { t[ m.uid ] = true })
        setTicks(t)
    }

    const resetForm = () => {
        setSelectedClassroom(null)
        setRoster([])
        setTicks({})
        setRosterSearch("")
        setSubjectCode("")
        setSubjectName("")
        setPeriod("")
        setStartTime("")
        setEndTime("")
        setPolicy(null)
        setDayRegisters([])
        setResultMsg(null)
        setResultIsError(false)
    }

    // Two modes, set by the classroom's policy: subject-wise (a subject must
    // be picked) or whole class (no subject; the server saves it against the
    // classroom's internal subject). With fixed slots, one must be picked.
    const slotMissing = slots.length > 0 && !slots.some(sl => sl.name === period)
    const subjectMissing = !noSubject && !subjectCode.trim()
    const canSubmit = !!selectedClassroom && !!lectureDate && roster.length > 0 && !submitting && !timeError && !loadingPolicy && !slotMissing && !subjectMissing

    const handleSubmit = async () => {
        if (!canSubmit || !selectedClassroom) return

        const present = roster.filter(m => ticks[ m.uid ]).map(m => ({ uid: m.uid, eid: m.eid, name: m.name }))
        const absent = roster.filter(m => !ticks[ m.uid ]).map(m => ({
            uid: m.uid, eid: m.eid, name: m.name,
            status: statuses[ m.uid ] || "absent",
        }))

        setSubmitting(true)
        setResultMsg(null)
        try {
            const res = await apiCall<{
                success: true; presentCount: number; absentCount: number; synced: boolean
            }>("attendance", "submitRegister", {
                method: "POST",
                body: {
                    classRoomId: selectedClassroom.id,
                    subjectCode: subjectCode.trim() || null,
                    subjectName: subjectCode.trim() ? (subjectName.trim() || null) : null,
                    lectureDate,
                    period: period.trim() || null,
                    startTime: startTime || null,
                    endTime: endTime || null,
                    present,
                    absent,
                },
            })
            setResultIsError(false)
            setResultMsg(`Saved — ${res.presentCount} present, ${res.absentCount} absent.${res.synced ? "" : " (sync pending)"}`)
            onSubmitted?.()
            loadDay()
            setTimeout(() => { setOpen(false); resetForm() }, 1400)
        } catch (e: any) {
            setResultIsError(true)
            setResultMsg(e.message || "Failed to submit register")
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <>
            <Button onClick={() => setOpen(true)} className="gap-2">
                <ClipboardCheck className="h-4 w-4" />
                Take Attendance
            </Button>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm() }}>
                <DialogContent className="sm:max-w-3xl max-h-[88vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold">Take Attendance</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-5 py-2">

                        {/* CLASSROOM */}
                        <div className="space-y-2">
                            <Label>Classroom</Label>
                            <ClassroomPicker value={selectedClassroom} onChange={selectClassroom} />
                        </div>

                        {/* LECTURE DETAILS */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Date</Label>
                                <Input type="date" value={lectureDate} onChange={e => setLectureDate(e.target.value)} />
                            </div>
                            {/* whole-class attendance: the date is all it needs */}
                            {!noSubject && (
                            <div className="space-y-2 sm:col-span-2">
                                <Label>Subject</Label>
                                <SubjectCombobox
                                    value={subjectCode}
                                    programmeId={selectedClassroom?.programme_id ?? null}
                                    placeholder="Search subject by code or name…"
                                    onSelect={(s) => {
                                        setSubjectCode(s.subject_code)
                                        setSubjectName(s.subject_name || "")
                                    }}
                                />
                                {selectedClassroom && subjectMissing && !loadingPolicy && (
                                    <p className="text-xs text-muted-foreground">Pick the subject this register is for.</p>
                                )}
                            </div>
                            )}
                        </div>

                        {/* one slot = picked already; whole class without slots = no lecture details */}
                        {slots.length === 1 || (noSubject && slots.length === 0) ? null : slots.length > 1 ? (
                            <div className="space-y-2">
                                <Label>Slot</Label>
                                <div className="flex flex-wrap gap-2">
                                    {slots.map(sl => {
                                        const taken = takenSlot(sl.name).length > 0
                                        return (
                                            <Button key={sl.name} type="button" size="sm" variant={period === sl.name ? "default" : "outline"}
                                                className="gap-1.5" onClick={() => pickSlot(sl)}>
                                                {taken && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                                                {sl.name}
                                                {sl.start && sl.end && <span className="flex items-center gap-1 text-xs opacity-80"><Clock className="h-3 w-3" /> {sl.start}–{sl.end}</span>}
                                            </Button>
                                        )
                                    })}
                                </div>
                                {selectedClassroom && (() => {
                                    const missing = slots.filter(sl => takenSlot(sl.name).length === 0).map(sl => sl.name)
                                    return missing.length > 0 && missing.length < slots.length
                                        ? <p className="text-xs text-amber-700">Not taken yet on this date: {missing.join(", ")}</p>
                                        : missing.length === 0 ? <p className="text-xs text-emerald-700">Every slot on this date has a register.</p> : null
                                })()}
                                {slotMissing && <p className="text-xs text-muted-foreground">Pick the slot this register is for.</p>}
                            </div>
                        ) : (
                        /* OPTIONAL: period label + time range (e.g. 9:00–11:00 and 11:20–1:00 on one day) */
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Period <span className="text-xs text-muted-foreground">(optional)</span></Label>
                                <Input value={period} onChange={e => setPeriod(e.target.value)} placeholder="e.g. 1" />
                            </div>
                            <div className="space-y-2">
                                <Label>Start time <span className="text-xs text-muted-foreground">(optional)</span></Label>
                                <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>End time <span className="text-xs text-muted-foreground">(optional)</span></Label>
                                <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
                            </div>
                            {timeError && <p className="sm:col-span-3 text-xs text-red-600 -mt-2">{timeError}</p>}
                        </div>
                        )}

                        {duplicates.length > 0 && (
                            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <div className="space-y-1">
                                    <p className="font-medium">
                                        A register for {selectedClassroom?.name}{period.trim() ? ` · ${period.trim()}` : ""} on {lectureDate} already exists.
                                    </p>
                                    {duplicates.map((r, i) => (
                                        <p key={i}>
                                            {r.mine ? "Taken by you" : `Shared with you by ${r.takenByName || "a colleague"}`}
                                            {r.subjectName || r.subjectCode ? ` (${r.subjectName || r.subjectCode})` : ""}.{" "}
                                            {onOpenRegister && r.sessionId && (
                                                <button type="button" className="font-medium underline" onClick={() => { setOpen(false); resetForm(); onOpenRegister(r.sessionId!) }}>Open it</button>
                                            )}
                                        </p>
                                    ))}
                                    <p className="text-xs">You can still submit; this is only a warning.</p>
                                </div>
                            </div>
                        )}

                        {(policy || loadingPolicy) && (
                            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                {loadingPolicy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Info className="h-3.5 w-3.5" />}
                                {loadingPolicy ? "Checking this classroom's attendance policy…" : `Set by the attendance policy${policy?.source ? ` “${policy.source}”` : ""}.`}
                            </p>
                        )}

                        <Separator />

                        {/* ROSTER */}
                        {loadingRoster && (
                            <p className="text-sm text-muted-foreground text-center py-6">Loading roster…</p>
                        )}

                        {!loadingRoster && selectedClassroom && roster.length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-6">This classroom has no students yet.</p>
                        )}

                        {!loadingRoster && roster.length > 0 && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <Label className="shrink-0">Roster ({roster.length} students) — tap to mark absent</Label>
                                    <Button variant="outline" size="sm" onClick={checkAllPresent}>
                                        Check all present
                                    </Button>
                                </div>
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                                    <Input
                                        placeholder="Find by name or ID..."
                                        value={rosterSearch}
                                        onChange={e => setRosterSearch(e.target.value)}
                                        className="pl-8 h-9 text-sm"
                                    />
                                </div>
                                {rosterSearch.trim() && (
                                    <p className="text-xs text-muted-foreground">{filteredRoster.length} of {roster.length} shown</p>
                                )}
                                <div className="rounded-lg border border-border/60 max-h-64 overflow-y-auto divide-y divide-border/40">
                                    {filteredRoster.length === 0 && (
                                        <p className="text-sm text-muted-foreground text-center py-6">No student matches "{rosterSearch}".</p>
                                    )}
                                    {filteredRoster.map(m => {
                                        const present = ticks[ m.uid ]
                                        const status = statuses[ m.uid ] || "absent"
                                        const joined = joinedLabel(m.joinedAt)
                                        const custom = !STATUS_OPTIONS.some(o => o.value === status)
                                        return (
                                            <div
                                                key={m.uid}
                                                className="w-full flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-muted/40 transition-colors"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() => setTicks(t => ({ ...t, [ m.uid ]: !t[ m.uid ] }))}
                                                    className="flex-1 text-left min-w-0"
                                                >
                                                    <span className="text-sm font-medium">
                                                        {m.name || m.uid}
                                                        {m.eid && <span className="ml-2 text-xs text-muted-foreground font-normal">({m.eid})</span>}
                                                    </span>
                                                    {/* When the student was put on the registery — context for a
                                                        backdated register, never a rule. */}
                                                    {joined && (
                                                        <span className="block text-[11px] text-muted-foreground">Registered {joined}</span>
                                                    )}
                                                </button>

                                                {present ? (
                                                    <Badge
                                                        variant="outline"
                                                        className="text-emerald-600 bg-emerald-50 border-emerald-200 cursor-pointer shrink-0"
                                                        onClick={() => setTicks(t => ({ ...t, [ m.uid ]: false }))}
                                                    >
                                                        PRESENT
                                                    </Badge>
                                                ) : (
                                                    <select
                                                        value={custom ? "__custom__" : status}
                                                        onChange={e => {
                                                            const v = e.target.value
                                                            if (v === "__custom__") {
                                                                const typed = window.prompt("Reason for this student (e.g. medical, sports meet)", custom ? status : "")
                                                                if (typed && typed.trim()) {
                                                                    setStatuses(st => ({ ...st, [ m.uid ]: typed.trim() }))
                                                                }
                                                                return
                                                            }
                                                            setStatuses(st => ({ ...st, [ m.uid ]: v }))
                                                        }}
                                                        className="shrink-0 h-7 text-xs rounded-md border border-red-200 bg-red-50 text-red-700 px-2"
                                                    >
                                                        {STATUS_OPTIONS.map(o => (
                                                            <option key={o.value} value={o.value}>{o.label}</option>
                                                        ))}
                                                        <option value="__custom__">{custom ? status : "Other…"}</option>
                                                    </select>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        {resultMsg && (
                            <p className={`text-sm ${resultIsError ? "text-red-600" : "text-emerald-600"}`}>
                                {resultMsg}
                            </p>
                        )}
                    </div>

                    <DialogFooter className="gap-2 pt-2">
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button className="min-w-[160px] gap-2" onClick={handleSubmit} disabled={!canSubmit}>
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            Submit Register
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
